import { useState, useEffect, FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import CryptoJS from 'crypto-js';
import { Button, Stack, Container, Tooltip } from '@mui/material';

import localAuth from 'model/auth/AuthStore';
import usePlan from 'model/auth/PlanStore';
import UpcomingCollection from 'components/collection/UpcomingCollection';
import Analytics from 'components/common/Analytics';
import CollectionWizard from 'components/collection/wizard/CollectionWizard';

import { create, getUpcomingCollection } from 'access/CollectingService';
import { getReport } from 'access/ReportingService';

import { PLUS } from 'assets/Icons'
import { Toolbar } from 'components/common/Toolbar';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { Loading } from 'components/common/Loading';
import { useApi } from 'hooks/useApi';
import { CollectionModel, CollectionReport } from 'model/collection/CollectionModels';
import { BACKEND_ENDPOINT, log } from 'utils/Common';
import { ErrorContainer } from 'components/common/ErrorContainer';

/**
 * NOW EVERY USER HAS REPORTS, EVEN NEW ONES. 
 * 
 * To determine if a user is new, we need to check the report activeCollectionCount == 0 if the report notificationsTotal == 0
 * 
 * if (report activeCollectionCount == 0 && report notificationsTotal == 0 && stored temp collection == 0) NEW_USER_SIGNUP -> show the CollectionWizard.
 * if (report activeCollectionCount == 0 && report notificationsTotal == 0 && stored temp collection != 0) NEW_USER_GET_STARTED -> save temp collection + show the analytics + upcoming collection
 * if (report activeCollectionCount == 0 && report notificationsTotal != 0) EXISTING_USER_NO_COLLECTION -> show the analytics
 * if (report activeCollectionCount != 0 && report notificationsTotal != 0) EXISTING_USER -> show the analytics + upcoming collection
 * if (report activeCollectionCount != 0 && report notificationsTotal == 0) EXISTING_USER_NO_NOTIFICATIONS -> show the analytics + upcoming collection
 */

const Dashboard: FunctionComponent = () => {
  const navigate = useNavigate();
  const collectionApi = useApi();
  const reportApi = useApi();

  const [collection, setCollection] = useState<CollectionModel | null>(null);
  const [report, setReport] = useState<CollectionReport | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const accessToken: string = localAuth((state) => state.authToken);
  const { collectionsLimit } = usePlan();

  useEffect(() => {
    const loadData = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        };

        const rep = await reportApi.request<CollectionReport>(() =>
          fetch(BACKEND_ENDPOINT + "reporting", { headers })
        );
        setReport(rep);

        // NEW_USER_GET_STARTED: activeCollectionCount == 0 && notificationsTotal == 0 && stored temp collection
        if (rep.activeCollectionCount === 0 && rep.notificationsTotal === 0) {
          const stored = localStorage.getItem("tempCollection");
          if (stored) {
            const key = await getFingerprint();
            const bytes = CryptoJS.AES.decrypt(stored, key);
            const decryptedMessage = bytes.toString(CryptoJS.enc.Utf8);
            log(decryptedMessage);
            await create(accessToken, JSON.parse(decryptedMessage));
            localStorage.removeItem("tempCollection");
            const newCol = await getUpcomingCollection(accessToken);
            const newRep = await getReport(accessToken);
            setCollection(newCol);
            setReport(newRep);
          }
          // NEW_USER_SIGNUP (no stored): stay, renderContent will show wizard
          return;
        }

        // EXISTING_USER or EXISTING_USER_NO_NOTIFICATIONS: fetch upcoming collection
        if (rep.activeCollectionCount > 0) {
          const col = await collectionApi.request<CollectionModel>(() =>
            fetch(BACKEND_ENDPOINT + "collecting/upcoming", { headers })
          );
          setCollection(col);
        }
      } catch (err) {
        log("Dashboard load error: " + err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [accessToken]);

  const atLimit = collectionsLimit !== null && (report?.activeCollectionCount ?? 0) >= collectionsLimit;

  const renderContent = () => {
    if (loading) return <Loading variant='walker' height='390px'/>;
    if (loadError) return <ErrorContainer text='An error occured. Please try again later.' height='390px'/>;

    // NEW_USER: activeCollectionCount == 0 && notificationsTotal == 0
    if (report && report.activeCollectionCount === 0 && report.notificationsTotal === 0) {
      log("NEW_USER");
      window.umami?.track('dashboard-loaded', { state: 'new-user' });
      return (
        <CollectionWizard
          cancelled={() => { window.umami?.track('wizard-cancelled'); navigate("/"); }}
          firstBackText="Back"
          onFinished={() => { window.umami?.track('wizard-completed'); window.location.reload(); }}
          finishedText='Create Collection'
          title="Start Collecting Now!"
          subtitle="Just fill out the information below, and click Next"/>
      );
    }

    // EXISTING_USER / EXISTING_USER_NO_NOTIFICATIONS: activeCollectionCount > 0
    if (report && report.activeCollectionCount > 0) {
      log("EXISTING_USER");
      window.umami?.track('dashboard-loaded', { state: 'existing-user' });
      return (
        <Stack spacing={1}>
          <Toolbar title='DASHBOARD&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'
            icon={<DashboardIcon style={{width: "24px", marginBottom: "-4px", marginRight: "5px"}}/>}
            items={[
              <Tooltip title={atLimit ? "Please contact us to create more collections" : ""} enterTouchDelay={0} leaveTouchDelay={3000}><span style={{display: "contents"}}>
                <Button variant='contained' color="inherit" disabled={atLimit} onClick={() => { window.umami?.track('new-collection-click'); navigate("/collection"); }}>{PLUS(20)}&nbsp;New Collection</Button>
              </span></Tooltip>
            ]}/>
          <Analytics report={report} upcomingCollection={collection ?? undefined}/>
          {collection && <UpcomingCollection collection={collection}/>}
        </Stack>
      );
    }

    // EXISTING_USER_NO_COLLECTION: activeCollectionCount == 0 && notificationsTotal != 0
    log("EXISTING_USER_NO_COLLECTION");
    window.umami?.track('dashboard-loaded', { state: 'no-collection' });
    return <Stack><Analytics report={report!}/></Stack>;
  };

  return (
    <Container maxWidth="md">
      {renderContent()}
    </Container>
  );
}

export default Dashboard;
